import React, { useMemo, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import APIHelper from "./utils/APIHelper";
import { toast } from "react-toastify";

export default function ProductForm({ token, onProductAdded, initialData }) {
  const [title, setTitle] = useState(initialData?.title || "");
  const [sku, setSku] = useState(initialData?.sku || "");
  const [price, setPrice] = useState(initialData?.price ?? "");
  const [compareAtPrice, setCompareAtPrice] = useState(initialData?.compareAtPrice ?? "");
  const [status, setStatus] = useState(initialData?.status || "Draft");
  const [isCheckoutUpsell, setIsCheckoutUpsell] = useState(initialData?.isCheckoutUpsell || false);
  const [description, setDescription] = useState(initialData?.description || "");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [variants, setVariants] = useState(
    initialData?.variants && initialData.variants.length > 0
      ? initialData.variants
      : [{ option: "", value: "" }]
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState(initialData?.thumbnailUrl || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [weightKg, setWeightKg] = useState(initialData?.weightKg ?? "");
  const [dimensionsCm, setDimensionsCm] = useState({
    length: initialData?.dimensionsCm?.length ?? "",
    width: initialData?.dimensionsCm?.width ?? "",
    height: initialData?.dimensionsCm?.height ?? "",
  });

  const isEdit = !!initialData?._id;

  const isFormValid = useMemo(
    () =>
      title.trim().length > 0 &&
      sku.trim().length > 0 &&
      !Number.isNaN(parseFloat(price)),
    [title, sku, price]
  );

  const addVariantRow = () =>
    setVariants((v) => [...v, { option: "", value: "" }]);
  const updateVariant = (idx, key, val) => {
    setVariants((prev) =>
      prev.map((v, i) => (i === idx ? { ...v, [key]: val } : v))
    );
  };
  const removeVariant = (idx) =>
    setVariants((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");
    let thumbnailUrl = initialData?.thumbnailUrl || "";
    try {
      if (thumbnailFile) {
        const uploaded = await APIHelper.uploadImageToCloudinary(thumbnailFile);
        thumbnailUrl = uploaded.secure_url;
      }
      const body = {
        title: title.trim(),
        sku: sku.trim(),
        price: parseFloat(price),
        compareAtPrice: compareAtPrice ? parseFloat(compareAtPrice) : 0,
        status,
        description,
        thumbnailUrl,
        variants: variants.filter((v) => v.option || v.value),
        currency: "INR",
        weightKg: weightKg ? parseFloat(weightKg) : null,
        dimensionsCm: {
          length: dimensionsCm.length ? parseFloat(dimensionsCm.length) : null,
          width: dimensionsCm.width ? parseFloat(dimensionsCm.width) : null,
          height: dimensionsCm.height ? parseFloat(dimensionsCm.height) : null,
        },
        isCheckoutUpsell,
      };

      if (isEdit) {
        await APIHelper.updateProduct(token, initialData._id, body);
        toast.success("Product updated successfully");
      } else {
        await APIHelper.createProduct(token, body);
        toast.success("Product created successfully");
      }

      // Reset form if not editing
      if (!isEdit) {
        setTitle("");
        setSku("");
        setPrice("");
        setCompareAtPrice("");
        setStatus("Draft");
        setIsCheckoutUpsell(false);
        setDescription("");
        setThumbnailFile(null);
        setVariants([{ option: "", value: "" }]);
        setPreviewUrl("");
        setWeightKg("");
        setDimensionsCm({ length: "", width: "", height: "" });
      }

      // Callback to refresh products list
      if (onProductAdded) {
        onProductAdded();
      }
    } catch (e) {
      const errorMsg =
        e?.response?.data?.message || e.message || `Failed to ${isEdit ? 'update' : 'create'} product`;
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card-modern p-6 md:p-8">
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-red-800 dark:text-red-200">
              ⚠️ {errorMessage}
            </Typography>
            <button
              onClick={() => setErrorMessage("")}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-green-800 dark:text-green-200">
              ✓ {successMessage}
            </Typography>
            <button
              onClick={() => setSuccessMessage("")}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-10">
            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
          </div>
          <div className="md:col-span-2">
            <FormControl fullWidth>
              <InputLabel id="product-status-label" className="text-gray-700 dark:text-gray-300">Status</InputLabel>
              <Select
                labelId="product-status-label"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '0.75rem',
                  },
                }}
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <TextField
            label="SKU"
            fullWidth
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            required
            className="bg-white dark:bg-gray-800"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
          <TextField
            label="Price (INR)"
            fullWidth
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputProps={{ inputMode: "decimal" }}
            helperText="Price in Indian Rupees (₹)"
            required
            className="bg-white dark:bg-gray-800"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
          <TextField
            label="Compare-at Price (INR)"
            fullWidth
            value={compareAtPrice}
            onChange={(e) => setCompareAtPrice(e.target.value)}
            inputProps={{ inputMode: "decimal" }}
            helperText="Compare-at price in Indian Rupees (₹)"
            className="bg-white dark:bg-gray-800"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />
        </div>
        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="bg-white dark:bg-gray-800"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.75rem',
            },
          }}
        />
        <div className="flex flex-col gap-1 p-4 bg-gray-50 dark:bg-gray-800/40 rounded-2xl border border-gray-100 dark:border-gray-800">
          <FormControlLabel
            control={
              <Checkbox
                checked={isCheckoutUpsell}
                onChange={(e) => setIsCheckoutUpsell(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography className="text-gray-900 dark:text-white font-semibold text-sm">
                Feature as checkout upsell
              </Typography>
            }
          />
          <Typography variant="body2" className="text-xs text-gray-500 dark:text-gray-400 pl-8">
            💡 Recommendation: Limit to 5 or fewer active upsell items to keep the checkout screen quick and clean for customers.
          </Typography>
        </div>
        <div>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            className="h-14 border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 normal-case rounded-xl font-medium"
          >
            {thumbnailFile ? "Change Thumbnail" : "Upload Thumbnail"}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setThumbnailFile(file);
                if (file) {
                  setPreviewUrl(URL.createObjectURL(file));
                }
              }}
            />
          </Button>
          {(thumbnailFile || previewUrl) && (
            <div className="mt-4 flex justify-center">
              <img
                src={previewUrl}
                alt="preview"
                className="max-h-32 rounded-xl border-2 border-primary-300 dark:border-primary-600 shadow-md"
              />
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
          <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Shipping Dimensions & Weight
          </Typography>
          <Typography variant="body2" className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Optional: Used for shipping label generation. If not provided,
            default shipping settings will be used.
          </Typography>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <TextField
                fullWidth
                label="Weight (kg)"
                type="number"
                value={weightKg}
                onChange={(e) => setWeightKg(e.target.value)}
                inputProps={{ min: 0.1, step: 0.1 }}
                helperText="Weight in kilograms"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Length (cm)"
                type="number"
                value={dimensionsCm.length}
                onChange={(e) =>
                  setDimensionsCm({ ...dimensionsCm, length: e.target.value })
                }
                inputProps={{ min: 1 }}
                helperText="Length in centimeters"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Width (cm)"
                type="number"
                value={dimensionsCm.width}
                onChange={(e) =>
                  setDimensionsCm({ ...dimensionsCm, width: e.target.value })
                }
                inputProps={{ min: 1 }}
                helperText="Width in centimeters"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Height (cm)"
                type="number"
                value={dimensionsCm.height}
                onChange={(e) =>
                  setDimensionsCm({ ...dimensionsCm, height: e.target.value })
                }
                inputProps={{ min: 1 }}
                helperText="Height in centimeters"
                className="bg-white dark:bg-gray-800"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
            </div>
          </div>
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
          <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Variants
          </Typography>
          <div className="space-y-4 mb-4">
            {variants.map((v, idx) => (
              <div
                key={idx}
                className="flex gap-4 items-start"
              >
                <TextField
                  label="Option"
                  value={v.option}
                  onChange={(e) => updateVariant(idx, "option", e.target.value)}
                  className="flex-1 bg-white dark:bg-gray-800"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.75rem',
                    },
                  }}
                />
                <TextField
                  label="Value"
                  value={v.value}
                  onChange={(e) => updateVariant(idx, "value", e.target.value)}
                  className="flex-1 bg-white dark:bg-gray-800"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.75rem',
                    },
                  }}
                />
                <Button
                  onClick={() => removeVariant(idx)}
                  disabled={variants.length === 1}
                  className="mt-1 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case disabled:opacity-50 rounded-xl"
                  sx={{ minWidth: '100px' }}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
          <Button
            onClick={addVariantRow}
            variant="outlined"
            className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case rounded-xl"
          >
            + Add Variant
          </Button>
        </div>
        <div className="flex justify-end mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="contained"
              disabled={!isFormValid || isSubmitting}
              onClick={handleSave}
              size="large"
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case disabled:opacity-50"
            >
              {isSubmitting ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Product" : "Create Product")}
            </Button>
        </div>
      </div>
    </div>
  );
}
